import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { SlidersHorizontal, X } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import VehicleCard from "@/components/VehicleCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "", label: "Todos" },
  { id: "suv", label: "SUV" },
  { id: "sedan", label: "Sedan" },
  { id: "hatchback", label: "Hatchback" },
  { id: "coupe", label: "Coupé" },
  { id: "carrinha", label: "Carrinha" },
  { id: "citadino", label: "Citadino" },
];
const FUELS = ["", "Gasolina", "Diesel", "Híbrido", "Elétrico", "GPL"];
const TRANSMISSIONS = ["", "Manual", "Automática"];

const initialFilters = {
  brand: "", model: "", price_min: "", price_max: "", year_min: "", year_max: "",
  km_max: "", fuel: "", transmission: "", category: "",
};

export default function CatalogPage() {
  const [vehicles, setVehicles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [applied, setApplied] = useState(initialFilters);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    axios.get(`${API}/vehicles/meta/brands`).then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    Object.entries(applied).forEach(([k, v]) => {
      if (v !== "" && v != null) params[k] = v;
    });
    axios
      .get(`${API}/vehicles`, { params })
      .then((r) => setVehicles(r.data))
      .catch(() => setVehicles([]))
      .finally(() => setLoading(false));
  }, [applied]);

  const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  const apply = (e) => {
    e?.preventDefault();
    setApplied({ ...filters });
    setShowFilters(false);
  };

  const clear = () => {
    setFilters(initialFilters);
    setApplied(initialFilters);
  };

  const activeCount = useMemo(
    () => Object.values(applied).filter((v) => v !== "").length,
    [applied]
  );

  const FiltersPanel = (
    <form onSubmit={apply} className="space-y-5" data-testid="catalog-filters">
      <div>
        <label htmlFor="f-brand" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Marca</label>
        <select id="f-brand" data-testid="f-brand" value={filters.brand} onChange={setF("brand")}
          className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]">
          <option value="">Todas</option>
          {brands.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="f-model" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Modelo</label>
        <input id="f-model" data-testid="f-model" value={filters.model} onChange={setF("model")} placeholder="Ex.: Golf, 320d…"
          className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="f-price-min" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Preço mín. (€)</label>
          <input id="f-price-min" data-testid="f-price-min" type="number" min={0} value={filters.price_min} onChange={setF("price_min")}
            className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
        </div>
        <div>
          <label htmlFor="f-price-max" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Preço máx. (€)</label>
          <input id="f-price-max" data-testid="f-price-max" type="number" min={0} value={filters.price_max} onChange={setF("price_max")}
            className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="f-year-min" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Ano de</label>
          <input id="f-year-min" data-testid="f-year-min" type="number" min={1950} value={filters.year_min} onChange={setF("year_min")}
            className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
        </div>
        <div>
          <label htmlFor="f-year-max" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Ano até</label>
          <input id="f-year-max" data-testid="f-year-max" type="number" min={1950} value={filters.year_max} onChange={setF("year_max")}
            className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
        </div>
      </div>
      <div>
        <label htmlFor="f-km-max" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Quilómetros até</label>
        <input id="f-km-max" data-testid="f-km-max" type="number" min={0} value={filters.km_max} onChange={setF("km_max")}
          className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" />
      </div>
      <div>
        <label htmlFor="f-fuel" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Combustível</label>
        <select id="f-fuel" data-testid="f-fuel" value={filters.fuel} onChange={setF("fuel")}
          className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]">
          <option value="">Todos</option>
          {FUELS.filter(Boolean).map((f) => <option key={f}>{f}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="f-trans" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Caixa</label>
        <select id="f-trans" data-testid="f-trans" value={filters.transmission} onChange={setF("transmission")}
          className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]">
          <option value="">Todas</option>
          {TRANSMISSIONS.filter(Boolean).map((t) => <option key={t}>{t}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="f-category" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Tipo de viatura</label>
        <select id="f-category" data-testid="f-category" value={filters.category} onChange={setF("category")}
          className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6]">
          {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>
      <div className="flex gap-3 pt-2">
        <button type="submit" data-testid="f-apply"
          className="flex-1 bg-[#0072C6] text-white font-semibold py-3.5 text-sm hover:bg-[#005FA3] transition-colors">
          Filtrar
        </button>
        <button type="button" onClick={clear} data-testid="f-clear"
          className="border border-slate-200 text-slate-600 font-semibold px-5 py-3.5 text-sm hover:border-[#0072C6] hover:text-[#0072C6] transition-colors">
          Limpar
        </button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <Header />
      <main className="pt-20">
        <div className="bg-[#F8FAFC] border-b border-slate-100 py-12">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <p className="text-xs font-semibold tracking-[0.35em] uppercase text-slate-500 mb-3">Catálogo</p>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-[#0072C6]" data-testid="catalog-heading">
              Automóveis à Venda
            </h1>
            <p className="mt-4 text-slate-600 max-w-2xl">
              Explore o nosso stock completo. Use os filtros para encontrar a viatura ideal.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
          <div className="lg:grid lg:grid-cols-12 lg:gap-10">
            {/* Sidebar filters (desktop) */}
            <aside className="hidden lg:block lg:col-span-3">
              <div className="sticky top-24 bg-white border border-slate-200 p-6" data-testid="filters-sidebar">
                <div className="flex items-center gap-2 mb-5">
                  <SlidersHorizontal className="w-4 h-4 text-[#0072C6]" />
                  <h2 className="font-display text-lg text-[#0072C6]">Pesquisar</h2>
                </div>
                {FiltersPanel}
              </div>
            </aside>

            {/* Mobile filter button */}
            <div className="lg:hidden mb-6">
              <button onClick={() => setShowFilters(true)} data-testid="open-filters-button"
                className="inline-flex items-center gap-2 bg-[#0072C6] text-white text-sm font-semibold px-5 py-3">
                <SlidersHorizontal className="w-4 h-4" /> Filtros
                {activeCount > 0 && <span className="bg-white text-[#0072C6] text-xs px-1.5 py-0.5">{activeCount}</span>}
              </button>
            </div>

            {/* Results */}
            <div className="lg:col-span-9">
              <p className="text-sm text-slate-500 mb-6" data-testid="catalog-count">
                {loading ? "A carregar…" : `${vehicles.length} viatura${vehicles.length !== 1 ? "s" : ""} encontrada${vehicles.length !== 1 ? "s" : ""}`}
              </p>
              {loading ? (
                <div className="py-20 text-center text-slate-400 text-sm" data-testid="catalog-loading">A carregar viaturas…</div>
              ) : vehicles.length === 0 ? (
                <div className="py-20 text-center border border-dashed border-slate-200" data-testid="catalog-empty">
                  <p className="text-slate-500">Nenhuma viatura corresponde aos filtros.</p>
                  <button onClick={clear} className="mt-4 text-sm font-semibold text-[#0072C6] underline">
                    Limpar filtros
                  </button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="catalog-grid">
                  {vehicles.map((v) => (
                    <VehicleCard key={v.id} vehicle={v} onEnquire={(veh) => { sessionStorage.setItem("lead_vehicle_id", veh.id); window.location.href = "/#contacto"; }} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />

      {/* Mobile filters drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto lg:hidden" data-testid="filters-drawer">
          <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between">
            <h2 className="font-display text-xl text-[#0072C6]">Filtros</h2>
            <button onClick={() => setShowFilters(false)} aria-label="Fechar" data-testid="close-filters-button">
              <X className="w-6 h-6 text-[#0072C6]" />
            </button>
          </div>
          <div className="p-5 pb-24">{FiltersPanel}</div>
        </div>
      )}
    </div>
  );
}
